import { useEffect, useRef, useState } from "react"
import { Label } from "@/components/ui/label.tsx"
import { Input } from "@/components/ui/input.tsx"

type LeafletMap = {
    setView: (latLng: [number, number], zoom: number) => void
    getZoom: () => number
    on: (event: string, callback: (event: any) => void) => void
    invalidateSize?: () => void
}

type LeafletMarker = {
    on: (event: string, callback: () => void) => void
    getLatLng: () => { lat: number; lng: number }
    setLatLng: (latLng: [number, number]) => void
}

interface LocationMapProps {
    address: string
    lat: string
    lng: string
    recenterKey: number
    addressSuggestions: Array<{ display_name: string; lat: string; lon: string }>
    searchingAdresse: boolean
    addressError: string
    fieldErrors?: {
        address?: string[]
        lat?: string[]
        lng?: string[]
    }
    onAdresseChange: (value: string) => void
    onAdresseSelect: (suggestion: { display_name: string; lat: string; lon: string }) => void
    onLatChange: (value: string) => void
    onLngChange: (value: string) => void
    onAdresseResolved: (value: string) => void
}

export default function LocationMap({
    address,
    lat,
    lng,
    recenterKey,
    addressSuggestions,
    searchingAdresse,
    addressError,
    fieldErrors,
    onAdresseChange,
    onAdresseSelect,
    onLatChange,
    onLngChange,
    onAdresseResolved,
}: LocationMapProps) {
    const [mapReady, setMapReady] = useState(false)
    const [mapError, setMapError] = useState("")
    const mapContainerRef = useRef<HTMLDivElement | null>(null)
    const mapRef = useRef<LeafletMap | null>(null)
    const markerRef = useRef<LeafletMarker | null>(null)
    const reverseLookupRequestIdRef = useRef(0)
    const lastRecenterKeyRef = useRef(recenterKey)

    useEffect(() => {
        if (typeof window === "undefined") {
            return
        }

        const existingLeaflet = (window as any).L
        if (existingLeaflet) {
            setMapReady(true)
            return
        }

        const stylesheetId = "leaflet-stylesheet"
        if (!document.getElementById(stylesheetId)) {
            const link = document.createElement("link")
            link.id = stylesheetId
            link.rel = "stylesheet"
            link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
            document.head.appendChild(link)
        }

        const scriptId = "leaflet-script"
        const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null
        if (existingScript) {
            existingScript.addEventListener("load", () => setMapReady(true))
            existingScript.addEventListener("error", () => setMapError("Impossible de charger la carte"))
            return
        }

        const script = document.createElement("script")
        script.id = scriptId
        script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"
        script.async = true
        script.onload = () => setMapReady(true)
        script.onerror = () => setMapError("Impossible de charger la carte")
        document.body.appendChild(script)
    }, [])

    const reverseGeocode = async (nextLat: number, nextLng: number) => {
        try {
            const requestId = reverseLookupRequestIdRef.current + 1
            reverseLookupRequestIdRef.current = requestId

            const response = await fetch(
                `/communities/reverse_geocode?lat=${encodeURIComponent(nextLat.toFixed(6))}&lng=${encodeURIComponent(nextLng.toFixed(6))}`
            )
            if (!response.ok) {
                return
            }

            const result = await response.json() as { display_name?: string }
            if (reverseLookupRequestIdRef.current !== requestId || !result.display_name) {
                return
            }

            onAdresseResolved(result.display_name)
        } catch (_error) {
            // Ignore reverse-geocoding failures to keep map interactions smooth.
        }
    }

    useEffect(() => {
        if (!mapReady || !mapContainerRef.current || mapRef.current || typeof window === "undefined") {
            return
        }

        const parsedLat = Number.parseFloat(lat)
        const parsedLng = Number.parseFloat(lng)
        const hasValidCoordinates = Number.isFinite(parsedLat) && Number.isFinite(parsedLng)
        const initialLat = hasValidCoordinates ? parsedLat : 0
        const initialLng = hasValidCoordinates ? parsedLng : 0
        const initialZoom = hasValidCoordinates ? 13 : 7
        const leaflet = (window as any).L

        if (!leaflet) {
            setMapError("Impossible de charger la carte")
            return
        }

        const map = leaflet.map(mapContainerRef.current).setView([initialLat, initialLng], initialZoom) as LeafletMap
        leaflet
            .tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
                maxZoom: 19,
                attribution: "&copy; OpenStreetMap contributors",
            })
            .addTo(map)

        const marker = leaflet.marker([initialLat, initialLng], { draggable: true }).addTo(map) as LeafletMarker

        marker.on("dragend", () => {
            const position = marker.getLatLng()
            onLatChange(position.lat.toFixed(6))
            onLngChange(position.lng.toFixed(6))
            void reverseGeocode(position.lat, position.lng)
        })

        map.on("click", (event: any) => {
            marker.setLatLng([event.latlng.lat, event.latlng.lng])
            onLatChange(event.latlng.lat.toFixed(6))
            onLngChange(event.latlng.lng.toFixed(6))
            void reverseGeocode(event.latlng.lat, event.latlng.lng)
        })

        mapRef.current = map
        markerRef.current = marker

        window.setTimeout(() => mapRef.current?.invalidateSize?.(), 50)
    }, [lat, lng, mapReady, onLatChange, onLngChange])

    useEffect(() => {
        if (!mapRef.current || !markerRef.current) {
            return
        }

        const parsedLat = Number.parseFloat(lat)
        const parsedLng = Number.parseFloat(lng)
        if (!Number.isFinite(parsedLat) || !Number.isFinite(parsedLng)) {
            return
        }

        markerRef.current.setLatLng([parsedLat, parsedLng])

        if (recenterKey !== lastRecenterKeyRef.current) {
            mapRef.current.setView([parsedLat, parsedLng], Math.max(13, mapRef.current.getZoom()))
            lastRecenterKeyRef.current = recenterKey
        }
    }, [lat, lng, recenterKey])

    return (
        <>
            <div className="space-y-2 relative">
                <Label htmlFor="address">Adresse</Label>
                <Input
                    type="text"
                    name="address"
                    id="address"
                    placeholder={"Adresse"}
                    value={address}
                    onChange={event => onAdresseChange(event.target.value)}
                    autoComplete="off"
                />
                {addressSuggestions.length > 0 && (
                    <div className="absolute z-20 mt-2 w-full rounded-md border bg-background shadow-sm">
                        {addressSuggestions.map(suggestion => (
                            <button
                                type="button"
                                key={`${suggestion.lat}-${suggestion.lon}-${suggestion.display_name}`}
                                className="block w-full cursor-pointer px-3 py-2 text-left text-sm hover:bg-muted"
                                onClick={() => onAdresseSelect(suggestion)}
                            >
                                {suggestion.display_name}
                            </button>
                        ))}
                    </div>
                )}
                {searchingAdresse && (
                    <div className="text-xs text-muted-foreground mt-2">Recherche d'adresse...</div>
                )}
                {addressError && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                        {addressError}
                    </div>
                )}
                {fieldErrors?.address && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                        {fieldErrors.address.join(", ")}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 gap-4 hidden">
                <div className="my-5">
                    <Label htmlFor="latitude">Latitude</Label>
                    <Input
                        type="text"
                        name="lat"
                        id="latitude"
                        value={lat}
                        onChange={event => onLatChange(event.target.value)}
                        placeholder={"Latitude"}
                    />
                    {fieldErrors?.lat && (
                        <div className="text-red-500 px-3 py-2 font-medium">
                            {fieldErrors.lat.join(", ")}
                        </div>
                    )}
                </div>

                <div className="my-5">
                    <Label htmlFor="longitude">Longitude</Label>
                    <Input
                        type="text"
                        name="lng"
                        id="longitude"
                        value={lng}
                        onChange={event => onLngChange(event.target.value)}
                        placeholder={"Longitude"}
                    />
                    {fieldErrors?.lng && (
                        <div className="text-red-500 px-3 py-2 font-medium">
                            {fieldErrors.lng.join(", ")}
                        </div>
                    )}
                </div>
            </div>

            <div className="my-5">
                <Label>Carte (glisser le marqueur ou cliquer)</Label>
                {mapError ? (
                    <div className="text-red-500 px-3 py-2 font-medium">{mapError}</div>
                ) : (
                    <div ref={mapContainerRef} className="mt-2 h-100 w-full rounded-md border" />
                )}
            </div>
        </>
    )
}
